/*
  ============================================================
  Oui Chef — Free Shipping Progress Bar
  File: assets/free-shipping-bar.js
  Version: 1.40.0
  Date: 2026-07-03
  ============================================================

  HOW IT WORKS
  - On page load, reads the threshold config embedded by Liquid
    in each .free-shipping-bar element's data attributes.
  - Fetches the current cart total from Shopify's /cart.js.
  - Calculates progress and updates all bar instances.
  - Listens for cart update events fired by Focal Carbon and
    re-runs the update whenever the cart changes.

  CURRENCY NOTE
  Shopify's /cart.js returns total_price in the store's base
  currency (EUR) in cents. Thresholds for non-EUR markets are
  stored as their EUR equivalent (data-threshold-base) so the
  comparison is always in EUR. The display amount shown to the
  customer is converted back to local currency using the ratio
  of data-threshold-display / data-threshold-base, which
  encodes the correct exchange rate for each zone.

  RE-RENDER STRATEGY
  Focal Carbon re-renders sections via Shopify's Section Rendering
  API after every cart change, replacing all bar DOM with fresh
  empty elements.

  CURRENCY (v1.12.0)
  Dynamic markets: JS reads window.Shopify.currency.rate (Shopify's
  live exchange rate, updated automatically) and formats amounts with
  Intl.NumberFormat. No hardcoded exchange rates or symbol tables.

  Fixed-rate markets (GB, US, CA, AU, NZ, HK, SG, AE): the Liquid
  snippet embeds data-threshold-display (the carrier-quoted local
  amount). JS uses the display/base ratio as the exchange rate for
  these markets, ignoring Shopify's live rate.

  CART DRAWER (sections/mini-cart.liquid)
  Focal Carbon calls Shopify's Section Rendering API — the section
  HTML is returned in the /cart/change.js response. watchForNewBars()
  detects the new bar element and populates it. Fill is pre-rendered
  by Liquid at the correct percentage (no 0%→X% animation needed).

  CART PAGE (sections/main-cart.liquid)
  v1.11.0 approach — no more location.reload():

  Focal Carbon already sends sections: ["mini-cart"] in its
  /cart/change.js request body. Our fetch interceptor piggybacks
  "main-cart" onto that same array, so Shopify returns the freshly-
  rendered main-cart section HTML in the very same response.

  patchCartReload() then intercepts window.location.reload() and:
    1. Fades out the cart section (0.2s)
    2. Replaces the section innerHTML with the pre-fetched HTML
    3. Fades it back in — no page navigation, no browser blank flash

  Falls back to a real reload (with fade) if the pre-fetch is
  unavailable for any reason.

  JS role:
    init()           — reads data-cart-total, populates message text
                       synchronously (no async fetch needed for this).
    watchForNewBars()— detects section re-renders in the cart drawer.
    updateAllBars()  — async verify; keeps cachedCartTotal fresh.
    patchCartReload()— intercepts reload for smooth in-place update.
  ============================================================
*/

(function () {
  'use strict';

  /* ── SVG paths ─────────────────────────────────────────── */

  const TRUCK_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <circle cx="7" cy="17" r="2"/>
    <circle cx="17" cy="17" r="2"/>
    <path d="M5 17H3V6a1 1 0 0 1 1-1h9v12M9 17h6M15 6h4.5L22 9.5V17h-2"/>
  </svg>`;

  const CHECK_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M5 13l4 4L19 7"/>
  </svg>`;

  const GIFT_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M20 12v10H4V12"/>
    <rect x="2" y="7" width="20" height="5" rx="1"/>
    <path d="M12 22V7"/>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>`;


  /* ── State ──────────────────────────────────────────────── */
  /*
     cachedCartTotal — last known cart total in EUR. Used to
     pre-populate newly-rendered bars synchronously.

     pendingCartUpdate — set by the fetch interceptor when it
     receives a fresh main-cart section render from Shopify (by
     piggybacking "main-cart" onto Focal Carbon's sections request).
     Consumed by patchCartReload() to do an in-place DOM swap instead
     of a real page reload. Cleared after use.
  */
  let cachedCartTotal      = null;
  let pendingCartUpdate    = null;   // { html: string, cart: object }
  let giftProductsCache    = null;   // cached gift products from Shopify collection
  let cachedCartItems      = [];     // [{ variant_id, product_id, quantity }] — updated on every fetch
  let giftPickerCollapsed  = false;  // persists user's open/close choice across picker re-renders

  /*
     Detect the main-cart section wrapper on the /cart page.
     We cannot rely on the section ID or file name because Shopify's JSON
     template format uses a template KEY (e.g. "main") as the wrapper ID
     — not the section file name (e.g. "main-cart"). The rendered element
     ends up as #shopify-section-main rather than #shopify-section-main-cart.
     Instead we locate the section by finding whichever shopify-section-*
     wrapper contains our free-shipping-bar but is NOT the mini-cart drawer
     and NOT an announcement bar variant.
  */
  const mainCartSectionEl = (function () {
    if (window.location.pathname.replace(/\/$/, '') !== '/cart') return null;

    // Primary: find the section containing the main cart recap form.
    // We use 'form.cart__recap' — it's unique to main-cart.liquid.
    // The mini-cart also has a form with action="/cart" (id="mini-cart-form")
    // but it has NO cart__recap class, so this selector can't match it.
    var cartForm = document.querySelector('form.cart__recap');
    if (cartForm) {
      var formSection = cartForm.closest('[id^="shopify-section-"]');
      if (formSection) return formSection;
    }

    // Secondary: find the section containing the gift picker that isn't
    // the announcement bar or mini-cart.
    var picker = document.querySelector('.free-shipping-bar__gift-picker');
    if (picker) {
      var pickerSection = picker.closest('[id^="shopify-section-"]');
      if (pickerSection
          && !pickerSection.id.includes('mini')
          && !pickerSection.id.includes('announcement')) return pickerSection;
    }

    // Fallback: find a bar not in announcement/mini sections.
    var found = null;
    document.querySelectorAll('.free-shipping-bar[data-threshold-base]').forEach(function (bar) {
      if (found) return;
      if (bar.classList.contains('free-shipping-bar--announcement')) return;
      var section = bar.closest('[id^="shopify-section-"]');
      if (section
          && !section.id.includes('mini')
          && !section.id.includes('announcement')) {
        found = section;
      }
    });
    return found;
  })();
  const mainCartSectionHandle = mainCartSectionEl
    ? mainCartSectionEl.id.replace('shopify-section-', '')
    : null;


  /* ── Fetch interceptor ──────────────────────────────────── */
  /*
     Two things happen here for every cart mutation request:

     1. cachedCartTotal is updated BEFORE Focal Carbon processes the
        response, so watchForNewBars() already has the right value
        when the newly-rendered section lands in the DOM.

     2. On the cart PAGE, we piggyback "main-cart" onto Focal Carbon's
        existing sections array (it always sends sections: ["mini-cart"]).
        Shopify then returns the freshly-rendered cart page HTML in the
        same response. We cache it in pendingCartUpdate so patchCartReload()
        can do an in-place DOM swap instead of a real page navigation.

     Only /cart/add, /cart/change, and /cart/update are intercepted.
     All other fetch calls are completely unaffected.
  */
  (function patchFetchForCartUpdates() {
    const CART_ENDPOINTS = ['/cart/add', '/cart/change', '/cart/update'];
    const originalFetch  = window.fetch;

    window.fetch = function (input, init) {
      const url    = input instanceof Request ? input.url : String(input);
      const isCart = CART_ENDPOINTS.some(ep => url.includes(ep));

      if (!isCart) return originalFetch.call(this, input, init);

      // On the cart page: add 'main-cart' to the sections array so Shopify
      // returns the freshly-rendered section HTML in this same response.
      // We only do this when the cart section wrapper is present on the page.
      let modifiedInit = init;
      if (mainCartSectionEl && mainCartSectionHandle && init && init.body) {
        try {
          const body = JSON.parse(init.body);
          if (Array.isArray(body.sections) && !body.sections.includes(mainCartSectionHandle)) {
            body.sections = body.sections.concat(mainCartSectionHandle);
            modifiedInit = Object.assign({}, init, { body: JSON.stringify(body) });
          }
        } catch (e) {}
      }

      return originalFetch.call(this, input, modifiedInit).then(function (response) {
        // Wrap .json() so our updates run before Focal Carbon processes the data
        const originalJson = response.json.bind(response);
        response.json = function () {
          return originalJson().then(function (data) {
            if (data && typeof data.total_price === 'number') {
              cachedCartTotal = data.total_price / 100;
            }
            if (data && Array.isArray(data.items)) {
              cachedCartItems = data.items.map(function (i) {
                return { variant_id: i.variant_id, product_id: i.product_id, quantity: i.quantity };
              });
            }
            // Cache the fresh main-cart HTML for the in-place DOM swap
            if (data && data.sections && data.sections[mainCartSectionHandle]) {
              pendingCartUpdate = { html: data.sections[mainCartSectionHandle], cart: data };
            }
            return data;
          });
        };
        return response;
      });
    };
  })();


  /* ── Fetch cart ─────────────────────────────────────────── */

  async function fetchCart() {
    // Some Shopify apps (e.g. lb-selleasy, shop_events_listener) patch window.fetch
    // and can break the chain. Wrap the call so a broken app wrapper never crashes us.
    let res;
    try {
      res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    } catch (e) {
      throw new Error('Cart fetch failed: ' + e.message);
    }
    if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
    const cart = await res.json();
    if (typeof cart.total_price === 'number') cachedCartTotal = cart.total_price / 100;
    if (Array.isArray(cart.items)) {
      cachedCartItems = cart.items.map(function (i) {
        return { variant_id: i.variant_id, product_id: i.product_id, quantity: i.quantity };
      });
    }
    return cart;
  }


  /* ── Country flag emoji ─────────────────────────────────── */
  /*
     Flag emojis are formed from pairs of Unicode Regional Indicator
     Symbol Letters (U+1F1E6–U+1F1FF). Each letter maps to an ISO 3166-1
     alpha-2 character via: codePoint = 0x1F1A5 + charCode(letter).
     e.g. 'I' + 'E' → 🇮 + 🇪 = 🇮🇪
     Works for every country code with no lookup table required.
  */

  function countryFlag(isoCode) {
    if (!isoCode || isoCode.length !== 2) return '';
    return [...isoCode.toUpperCase()]
      .map(c => String.fromCodePoint(0x1F1A5 + c.charCodeAt(0)))
      .join('');
  }


  /* ── Update a single bar element ─────────────────── */
  /*
     Exchange rate logic:
       Fixed-rate markets (GB, US, CA, etc.) — Liquid embeds
       data-threshold-display (carrier-quoted local amount). JS uses
       the display/base ratio so the shown amount never drifts with
       exchange rates.

       Dynamic markets (all others) — data-threshold-display is absent.
       JS reads window.Shopify.currency.rate, which Shopify keeps
       current automatically.

     Formatting:
       Intl.NumberFormat with style:'currency' handles symbol position,
       decimal places, and thousands separators for every currency.
       No lookup tables or special-casing needed.
  */

  function updateBar(el, cartTotalDisplay) {
    const thresholdBase    = parseFloat(el.dataset.thresholdBase);    // EUR
    const thresholdDisplay = parseFloat(el.dataset.thresholdDisplay); // local (fixed-rate only)
    const currencyIso      = el.dataset.currencyIso || 'EUR';

    const countryName  = el.dataset.countryName  || '';
    const msgGoal      = (el.dataset.msgGoal      || 'Free shipping to {country} on orders over {amount}').replace('{country}', countryName);
    const msgProgress  = (el.dataset.msgProgress  || "You're {amount} away from free shipping").replace('{country}', countryName);
    const msgQualified = (el.dataset.msgQualified || "You've unlocked free shipping").replace('{country}', countryName);

    const fill   = el.querySelector('.free-shipping-bar__fill');
    const msgEl  = el.querySelector('.free-shipping-bar__message');
    const iconEl = el.querySelector('.free-shipping-bar__icon');

    if (!fill || !msgEl || !iconEl) return;

    // Shopify's live exchange rate (display currency → EUR).
    // cart.total_price from both Liquid and /cart.js is in the customer's
    // display currency, so we divide here to get the EUR equivalent.
    // For EUR customers shopifyRate === 1, so this is a no-op.
    const shopifyRate  = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    const cartTotalEur = cartTotalDisplay / shopifyRate;

    // Exchange rate for display: fixed-rate markets use display/base ratio;
    // dynamic markets use Shopify's live rate.
    const hasFixed     = !isNaN(thresholdDisplay) && thresholdDisplay > 0;
    const exchangeRate = hasFixed ? thresholdDisplay / thresholdBase : shopifyRate;

    const threshDisplay  = hasFixed ? thresholdDisplay : Math.round(thresholdBase * shopifyRate);
    const remainingEur   = Math.max(0, thresholdBase - cartTotalEur);
    const remainingLocal = Math.ceil(remainingEur * exchangeRate);
    const pct            = Math.min(100, (cartTotalEur / thresholdBase) * 100);
    const qualified      = remainingEur <= 0;

    // Intl.NumberFormat: correct symbol, position, separators for every currency.
    var fmt;
    try {
      fmt = new Intl.NumberFormat([], {
        style:                 'currency',
        currency:              currencyIso,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    } catch (e) {
      fmt = { format: function (n) { return currencyIso + ' ' + Math.round(n); } };
    }

    // Progress fill
    fill.style.width = pct.toFixed(1) + '%';

    // Gift feature
    const giftEnabled    = el.dataset.giftEnabled === 'true';
    const giftThreshBase = parseFloat(el.dataset.giftThresholdBase) || 0;
    const giftQualified  = giftEnabled && giftThreshBase > 0 && cartTotalEur >= giftThreshBase;

    // Milestone circles — update reached state
    const giftMilestone     = el.querySelector('.free-shipping-bar__milestone--gift');
    const shippingMilestone = el.querySelector('.free-shipping-bar__milestone--shipping');
    if (giftMilestone)     giftMilestone.classList.toggle('free-shipping-bar__milestone--reached',     giftQualified);
    if (shippingMilestone) shippingMilestone.classList.toggle('free-shipping-bar__milestone--reached', qualified);

    // Gift message strings
    const msgGiftGoal     = (el.dataset.msgGiftGoal     || 'Spend {gift_amount} to unlock a free gift').replace('{country}', countryName);
    const msgGiftUnlocked = (el.dataset.msgGiftUnlocked || '🎁 Free gift unlocked! {amount} to go for free shipping').replace('{country}', countryName);
    const msgBothUnlocked = (el.dataset.msgBothUnlocked || '🎁 Free gift + free shipping unlocked!').replace('{country}', countryName);

    // Message state
    if (qualified) {
      el.classList.add('free-shipping-bar--qualified');
      iconEl.innerHTML = CHECK_SVG;
      msgEl.innerHTML  = giftEnabled ? msgBothUnlocked : msgQualified;

    } else if (giftQualified) {
      // Gift threshold met, working toward free shipping
      el.classList.remove('free-shipping-bar--qualified');
      iconEl.innerHTML = GIFT_SVG;
      msgEl.innerHTML  = msgGiftUnlocked.replace(
        '{amount}', '<strong>' + fmt.format(remainingLocal) + '</strong>'
      );

    } else if (giftEnabled && giftThreshBase > 0) {
      // Gift not yet unlocked — show gift goal
      el.classList.remove('free-shipping-bar--qualified');
      iconEl.innerHTML = GIFT_SVG;
      const giftRemEur   = Math.max(0, giftThreshBase - cartTotalEur);
      const giftRemLocal = Math.ceil(giftRemEur * exchangeRate);
      const giftFullLocal = Math.round(giftThreshBase * exchangeRate);
      if (cartTotalEur === 0) {
        msgEl.innerHTML = msgGiftGoal.replace(
          '{gift_amount}', '<strong>' + fmt.format(giftFullLocal) + '</strong>'
        );
      } else {
        msgEl.innerHTML = msgGiftGoal.replace(
          '{gift_amount}', '<strong>' + fmt.format(giftRemLocal) + '</strong>'
        );
      }

    } else {
      // Gift not enabled — original free shipping states
      el.classList.remove('free-shipping-bar--qualified');
      iconEl.innerHTML = TRUCK_SVG;
      if (cartTotalEur === 0) {
        msgEl.innerHTML = msgGoal.replace(
          '{amount}', '<strong>' + fmt.format(threshDisplay) + '</strong>'
        );
      } else {
        msgEl.innerHTML = msgProgress.replace(
          '{amount}', '<strong>' + fmt.format(remainingLocal) + '</strong>'
        );
      }
    }

    // Country flag after the message
    const flag = countryFlag(el.dataset.country);
    if (flag) msgEl.innerHTML += ' ' + flag;

    // Mirror message to announcement bar placeholders (after flag is appended)
    document.querySelectorAll('[data-fsb-announcement]').forEach(function (ann) {
      ann.innerHTML = msgEl.innerHTML;
    });

    // Show / refresh gift picker
    if (giftEnabled && giftThreshBase > 0) {
      const picker = el.querySelector('.free-shipping-bar__gift-picker');
      if (picker) {
        if (giftQualified) {
          picker.hidden = false;
          refreshGiftPicker(el, picker);
        } else {
          picker.hidden = true;
        }
      }
    }

    // Accessibility
    const track = el.querySelector('.free-shipping-bar__track');
    if (track) {
      track.setAttribute('aria-valuenow', Math.round(pct));
      track.setAttribute('aria-valuetext', qualified
        ? 'Free shipping unlocked'
        : fmt.format(remainingLocal) + ' remaining'
      );
    }
  }


  /* ── Gift picker helpers ────────────────────────────────── */
  /*
     refreshGiftPicker — called from updateBar() whenever the gift
     threshold is met. Re-renders the picker with the latest cart
     state (showing "Added ✓" if the gift is already in cart).

     Skips bars that live inside a [hidden] container — this guards
     against the invisible helper bar embedded in announcement-bar.liquid,
     which should never trigger product fetches.

     Gift products are fetched once from the store's "free-gifts"
     collection (or the handle set in Theme Editor) and cached for
     the session. Re-renders are synchronous after the first load.
  */

  function refreshGiftPicker(bar, picker) {
    // Don't render for the hidden helper bar inside announcement-bar.liquid
    var node = bar.parentElement;
    while (node) {
      if (node.hidden || node.hasAttribute && node.hasAttribute('hidden')) return;
      node = node.parentElement;
    }

    if (giftProductsCache) {
      renderGiftPicker(picker, giftProductsCache);
    } else {
      loadGiftPickerAsync(bar, picker);
    }
  }

  function loadGiftPickerAsync(bar, picker) {
    var collection = bar.dataset.giftCollection || 'free-gifts';
    fetch('/collections/' + collection + '/products.json?limit=10')
      .then(function (res) {
        if (!res.ok) throw new Error('Gift collection "' + collection + '" not found (HTTP ' + res.status + ')');
        return res.json();
      })
      .then(function (data) {
        giftProductsCache = data.products || [];
        renderGiftPicker(picker, giftProductsCache);
      })
      .catch(function (e) {
        console.warn('[FreeShippingBar] Could not load gift products:', e.message);
        giftProductsCache = [];
      });
  }

  function renderGiftPicker(picker, products) {
    if (!products.length) {
      picker.innerHTML = '<p class="free-shipping-bar__gift-header">Choose your free gift</p>';
      return;
    }

    // Filter to products that have at least one available variant
    products = products.filter(function (p) {
      return p.variants.some(function (v) { return v.available; });
    });

    if (!products.length) {
      picker.innerHTML = '';
      return;
    }

    var isOpen = !giftPickerCollapsed;
    var html = '<button type="button" class="free-shipping-bar__gift-toggle" aria-expanded="' + (isOpen ? 'true' : 'false') + '">';
    html += '<span>Choose your free gift</span>';
    html += '<svg class="free-shipping-bar__gift-toggle-chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
    html += '</button>';
    html += '<div class="free-shipping-bar__gift-products' + (giftPickerCollapsed ? ' free-shipping-bar__gift-products--collapsed' : '') + '">';

    // If ANY gift product is already in cart, lock all "Add" buttons — one gift per order
    var anyGiftInCart = products.some(function (p) {
      return p.variants.some(function (v) {
        return cachedCartItems.some(function (ci) { return ci.variant_id === v.id; });
      });
    });

    products.forEach(function (product) {
      // Find which specific variant of this product is already in the cart
      var inCartVariant = null;
      product.variants.forEach(function (v) {
        if (!inCartVariant) {
          var found = cachedCartItems.find(function (ci) { return ci.variant_id === v.id; });
          if (found) inCartVariant = v;
        }
      });
      var inCart = !!inCartVariant;

      var availableVariants = product.variants.filter(function (v) { return v.available; });
      var hasVariants = availableVariants.length > 1;
      var defVariant  = availableVariants[0];

      // Shopify CDN image URL — 80×80 thumbnail + original for lightbox
      var imgSrc = '';
      var imgLargeSrc = '';
      if (product.images && product.images[0] && product.images[0].src) {
        imgLargeSrc = product.images[0].src;
        imgSrc = product.images[0].src.replace(/(\.[a-z]+)(\?|$)/i, '_80x80$1$2');
      }

      html += '<div class="free-shipping-bar__gift-product" data-product-id="' + product.id + '">';

      if (imgSrc) {
        html += '<button type="button" class="free-shipping-bar__gift-product-img-btn" data-img-url="' + escapeAttr(imgLargeSrc) + '" data-img-alt="' + escapeAttr(product.title) + '" aria-label="View ' + escapeAttr(product.title) + ' image">';
        html += '<img class="free-shipping-bar__gift-product-img" src="' + escapeAttr(imgSrc) + '" alt="" loading="lazy">';
        html += '<span class="free-shipping-bar__gift-product-img-zoom" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>';
        html += '</button>';
      }

      html += '<div class="free-shipping-bar__gift-product-info">';
      html += '<span class="free-shipping-bar__gift-product-title">' + escapeHtml(product.title) + '</span>';

      if (hasVariants) {
        var disabled = anyGiftInCart ? ' disabled' : '';
        html += '<div class="fsb-variant-select' + (anyGiftInCart ? ' fsb-variant-select--disabled' : '') + '" data-fsb-select>';
        html += '<button type="button" class="fsb-variant-select__btn" aria-haspopup="listbox" aria-expanded="false"' + disabled + '>';
        html += '<span class="fsb-variant-select__label">' + escapeHtml(availableVariants[0].title) + '</span>';
        html += '<svg class="fsb-variant-select__chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="11" height="11" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
        html += '</button>';
        html += '<ul class="fsb-variant-select__list" role="listbox" hidden>';
        availableVariants.forEach(function (v, i) {
          var varImgSrc = (v.featured_image && v.featured_image.src) ? v.featured_image.src : '';
          html += '<li class="fsb-variant-select__option' + (i === 0 ? ' fsb-variant-select__option--active' : '') + '" role="option" data-value="' + v.id + '"' + (varImgSrc ? ' data-img="' + escapeAttr(varImgSrc) + '"' : '') + ' aria-selected="' + (i === 0 ? 'true' : 'false') + '">' + escapeHtml(v.title) + '</li>';
        });
        html += '</ul>';
        // Hidden native select — keeps existing add/remove JS working unchanged
        html += '<select class="free-shipping-bar__gift-variant-select" aria-hidden="true" tabindex="-1" style="display:none"' + disabled + '>';
        availableVariants.forEach(function (v) {
          html += '<option value="' + v.id + '">' + escapeHtml(v.title) + '</option>';
        });
        html += '</select>';
        html += '</div>';
      }

      if (inCart) {
        // Show Remove so the customer can swap their choice
        html += '<button class="free-shipping-bar__gift-add-btn free-shipping-bar__gift-remove-btn" data-variant-id="' + inCartVariant.id + '">Remove</button>';
      } else if (anyGiftInCart) {
        // Another gift already chosen — disable without "added" styling
        html += '<button class="free-shipping-bar__gift-add-btn" disabled style="opacity:0.35">Add free gift</button>';
      } else {
        html += '<button class="free-shipping-bar__gift-add-btn" data-variant-id="' + defVariant.id + '">Add free gift</button>';
      }

      html += '</div></div>';
    });

    html += '</div>'; // close .free-shipping-bar__gift-products

    picker.innerHTML = html;

    // Wire up collapse toggle
    var toggleBtn    = picker.querySelector('.free-shipping-bar__gift-toggle');
    var productsGrid = picker.querySelector('.free-shipping-bar__gift-products');
    if (toggleBtn && productsGrid) {
      toggleBtn.addEventListener('click', function () {
        giftPickerCollapsed = !giftPickerCollapsed;
        toggleBtn.setAttribute('aria-expanded', giftPickerCollapsed ? 'false' : 'true');
        productsGrid.classList.toggle('free-shipping-bar__gift-products--collapsed', giftPickerCollapsed);
      });
    }

    // Wire up custom variant dropdowns
    // The list is portalled to document.body when open so it escapes the
    // overflow:hidden on .free-shipping-bar__gift-products (needed for the
    // collapse animation). position:fixed + getBoundingClientRect keeps it
    // visually anchored to the button.
    var drawerId = (function () {
      var el = document.querySelector('cart-drawer[id]');
      return el ? el.id : null;
    })();

    picker.querySelectorAll('[data-fsb-select]').forEach(function (wrapper) {
      var dropBtn  = wrapper.querySelector('.fsb-variant-select__btn');
      var dropList = wrapper.querySelector('.fsb-variant-select__list');
      var labelEl  = wrapper.querySelector('.fsb-variant-select__label');
      var natSel   = wrapper.querySelector('select.free-shipping-bar__gift-variant-select');
      if (!dropBtn || !dropList) return;

      // Add aria-controls to list + options so Focal Carbon's focus-trap
      // doesn't close the cart drawer when the user clicks inside the portal
      if (drawerId) {
        dropList.setAttribute('aria-controls', drawerId);
        dropList.querySelectorAll('.fsb-variant-select__option').forEach(function (o) {
          o.setAttribute('aria-controls', drawerId);
        });
      }

      function openDropList() {
        // Close all other open dropdowns first
        document.querySelectorAll('.fsb-variant-select__btn[aria-expanded="true"]').forEach(function (b) {
          if (b !== dropBtn && b._fsbClose) b._fsbClose();
        });
        var rect = dropBtn.getBoundingClientRect();
        dropList.style.cssText = 'position:fixed;top:' + (rect.bottom + 3) + 'px;left:' + rect.left + 'px;width:' + rect.width + 'px;right:auto;z-index:9999;';
        document.body.appendChild(dropList);
        dropList.hidden = false;
        dropBtn.setAttribute('aria-expanded', 'true');
      }

      function closeDropList() {
        dropBtn.setAttribute('aria-expanded', 'false');
        dropList.hidden = true;
        wrapper.appendChild(dropList); // return to original parent
        dropList.style.cssText = '';
      }

      dropBtn._fsbClose = closeDropList; // stored for global close handler

      dropBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (dropBtn.getAttribute('aria-expanded') === 'true') {
          closeDropList();
        } else {
          openDropList();
        }
      });

      dropList.querySelectorAll('.fsb-variant-select__option').forEach(function (opt) {
        opt.addEventListener('click', function (e) {
          e.stopPropagation();
          labelEl.textContent = opt.textContent;
          if (natSel) {
            natSel.value = opt.dataset.value;
            natSel.dispatchEvent(new Event('change')); // triggers existing variant-id sync
          }
          // Update active state
          dropList.querySelectorAll('.fsb-variant-select__option').forEach(function (o) {
            o.classList.toggle('fsb-variant-select__option--active', o === opt);
            o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
          });
          // Update product thumbnail + quick-view URL if this variant has its own image
          var varImg = opt.dataset.img;
          if (varImg) {
            var card   = wrapper.closest('.free-shipping-bar__gift-product');
            var imgEl  = card && card.querySelector('.free-shipping-bar__gift-product-img');
            var imgBtn = card && card.querySelector('.free-shipping-bar__gift-product-img-btn');
            if (imgEl) imgEl.src = varImg.replace(/(\.[a-z]+)(\?|$)/i, '_80x80$1$2');
            if (imgBtn) imgBtn.dataset.imgUrl = varImg;
          }
          closeDropList();
        });
      });
    });

    // Wire up add buttons (skip remove buttons — they have their own handler below)
    picker.querySelectorAll('.free-shipping-bar__gift-add-btn:not([disabled]):not(.free-shipping-bar__gift-remove-btn)').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card   = btn.closest('.free-shipping-bar__gift-product');
        var sel    = card && card.querySelector('.free-shipping-bar__gift-variant-select');
        var varId  = parseInt(sel ? sel.value : btn.dataset.variantId, 10);
        addGiftToCart(varId, btn);
      });
    });

    // Wire up remove buttons
    picker.querySelectorAll('.free-shipping-bar__gift-remove-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeGiftFromCart(parseInt(btn.dataset.variantId, 10), btn);
      });
    });

    // Keep data-variant-id in sync when variant selector changes
    picker.querySelectorAll('.free-shipping-bar__gift-variant-select').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var card = sel.closest('.free-shipping-bar__gift-product');
        var btn  = card && card.querySelector('.free-shipping-bar__gift-add-btn');
        if (btn) btn.dataset.variantId = sel.value;
      });
    });
  }

  /*
     swapCartSections — applies fresh section HTML returned by Shopify when
     sections are included in a cart mutation request body.

     Cart PAGE (main-cart): fade out (0.2s), swap innerHTML, fade in — same
       animation as patchCartReload. Works regardless of whether Focal Carbon
       patches window.location.reload after our init() runs.
     Cart DRAWER (mini-cart): surgical swap — update only the <cart-drawer>
       children so the element itself (and its open state) is preserved.
     Fallback: if no HTML came back in the response, fetch it explicitly.
  */
  /*
     updateAllVisiblePickers — immediately re-renders every gift picker that
     is currently visible, using only the current cachedCartItems state.
     Does not touch the cart items list or any Focal Carbon DOM — guaranteed
     to work regardless of DOM structure or whether section HTML is available.
     Called first in add/remove handlers so the button ALWAYS updates, even
     when the section swap below can't find the right element.
  */
  function updateAllVisiblePickers() {
    if (!giftProductsCache) return;
    document.querySelectorAll('.free-shipping-bar__gift-picker:not([hidden])').forEach(function (picker) {
      renderGiftPicker(picker, giftProductsCache);
    });
  }

  function swapCartSections(miniCartHtml, mainCartHtml) {
    // mainCartSectionEl is discovered at init from the live DOM — works
    // regardless of whether the section file is named main-cart.liquid, cart.liquid, etc.
    var mainWrapper = mainCartSectionEl;

    if (mainWrapper) {
      if (mainCartHtml) {
        mainWrapper.classList.add('fsb-section-exit');
        setTimeout(function () {
          mainWrapper.classList.remove('fsb-section-exit');
          mainWrapper.innerHTML = mainCartHtml;
          mainWrapper.classList.add('fsb-section-enter');
        }, 220);
      } else {
        // On cart page but no section HTML in response — smooth reload fallback.
        mainWrapper.classList.add('fsb-section-exit');
        setTimeout(function () {
          window.location.href = window.location.href;
        }, 230);
      }
    }

    // Cart drawer — surgical swap so the drawer stays open.
    if (miniCartHtml) {
      var drawerTmp = document.createElement('div');
      drawerTmp.innerHTML = miniCartHtml;
      var live  = document.querySelector('#shopify-section-mini-cart cart-drawer');
      var fresh = drawerTmp.querySelector('cart-drawer');
      if (live && fresh) {
        live.innerHTML = fresh.innerHTML;
      }
    }
  }

  /*
     refreshMiniCartDrawer — last-resort fallback when section HTML is not
     available in the response. Fetches /?sections=mini-cart and does the
     surgical drawer swap.
  */
  function refreshMiniCartDrawer() {
    return Promise.all([
      fetch('/?sections=mini-cart').then(function (r) { return r.json(); }),
      fetchCart()
    ])
    .then(function (results) {
      var data = results[0];
      if (!data || !data['mini-cart']) return;
      var tmp  = document.createElement('div');
      tmp.innerHTML = data['mini-cart'];
      var live  = document.querySelector('#shopify-section-mini-cart cart-drawer');
      var fresh = tmp.querySelector('cart-drawer');
      if (live && fresh) {
        live.innerHTML = fresh.innerHTML;
      } else {
        var wrapper = document.getElementById('shopify-section-mini-cart');
        if (wrapper) wrapper.innerHTML = data['mini-cart'];
      }
    })
    .catch(function () {});
  }

  function addGiftToCart(variantId, btn) {
    if (!variantId) return;
    btn.disabled    = true;
    btn.textContent = 'Adding…';

    // Request sections so Shopify returns fresh HTML in this same response —
    // no second round-trip needed. Request the main-cart section too when on
    // the cart page. mainCartSectionHandle is discovered at init from the DOM,
    // so it works regardless of the section file name (main-cart.liquid, cart.liquid…).
    var onCartPage = !!mainCartSectionEl;
    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        id:       variantId,
        quantity: 1,
        sections: (onCartPage && mainCartSectionHandle) ? ['mini-cart', mainCartSectionHandle] : ['mini-cart']
      })
    })
    .then(function (res) {
      if (!res.ok) {
        return res.json().then(function (d) { throw new Error(d.description || 'Add failed'); });
      }
      return res.json();
    })
    .then(function (data) {
      // /cart/add.js returns only the added line item (not the full cart),
      // so the interceptor cannot update cachedCartItems — do it manually.
      if (!cachedCartItems.some(function (ci) { return ci.variant_id === variantId; })) {
        cachedCartItems.push({ variant_id: variantId, product_id: 0, quantity: 1 });
      }
      // -- DIAGNOSTIC --
      console.warn('[FSB add]', {
        sectionEl: mainCartSectionEl ? mainCartSectionEl.id : 'NOT FOUND',
        handle: mainCartSectionHandle,
        sectionKeys: data && data.sections ? Object.keys(data.sections) : [],
        visiblePickers: document.querySelectorAll('.free-shipping-bar__gift-picker:not([hidden])').length,
        giftCache: !!giftProductsCache
      });
      // ----------------
      // Re-render picker immediately — guaranteed to update the button state
      // regardless of whether the section swap below succeeds.
      updateAllVisiblePickers();
      return swapCartSections(
        data && data.sections && data.sections['mini-cart'],
        data && data.sections && data.sections[mainCartSectionHandle]
      );
    })
    .catch(function (e) {
      btn.disabled    = false;
      btn.textContent = 'Add free gift';
      console.warn('[FreeShippingBar] Gift add failed:', e.message);
    });
  }

  function removeGiftFromCart(variantId, btn) {
    if (!variantId) return;
    btn.disabled    = true;
    btn.textContent = 'Removing…';

    // Decrement by one so a product also in the cart at full price is not
    // accidentally wiped out entirely.
    var currentItem = cachedCartItems.find(function (ci) { return ci.variant_id === variantId; });
    var newQty = (currentItem && currentItem.quantity > 1) ? currentItem.quantity - 1 : 0;

    // Use /cart/update.js (not /cart/change.js) — Focal Carbon watches
    // /cart/change.js for mini-cart updates; /cart/update.js avoids triggering
    // their handler which would do a competing full section replace.
    var updates = {};
    updates[variantId] = newQty;
    var onCartPage = !!mainCartSectionEl;
    fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        updates:  updates,
        sections: (onCartPage && mainCartSectionHandle) ? ['mini-cart', mainCartSectionHandle] : ['mini-cart']
      })
    })
    .then(function (res) {
      if (!res.ok) {
        return res.json().then(function (d) { throw new Error(d.description || 'Remove failed'); });
      }
      return res.json();
    })
    .then(function (data) {
      // cachedCartItems already updated by the interceptor (/cart/update.js
      // returns the full cart including items and total_price).
      // -- DIAGNOSTIC --
      console.warn('[FSB remove]', {
        sectionEl: mainCartSectionEl ? mainCartSectionEl.id : 'NOT FOUND',
        handle: mainCartSectionHandle,
        sectionKeys: data && data.sections ? Object.keys(data.sections) : [],
        visiblePickers: document.querySelectorAll('.free-shipping-bar__gift-picker:not([hidden])').length,
        giftCache: !!giftProductsCache
      });
      // ----------------
      // Re-render picker immediately — guaranteed to update the button state
      // regardless of whether the section swap below succeeds.
      updateAllVisiblePickers();
      return swapCartSections(
        data && data.sections && data.sections['mini-cart'],
        data && data.sections && data.sections[mainCartSectionHandle]
      );
    })
    .catch(function (e) {
      btn.disabled    = false;
      btn.textContent = 'Remove';
      console.warn('[FreeShippingBar] Gift remove failed:', e.message);
    });
  }

  function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  /* ── Update all bar instances on the page ───────────────── */

  async function updateAllBars() {
    const bars = document.querySelectorAll('.free-shipping-bar[data-threshold-base]');
    if (!bars.length) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      console.warn('[FreeShippingBar] Could not fetch cart:', e);
      return;
    }

    // /cart.js total_price is in display currency cents (same as Liquid).
    // updateBar() divides by shopifyRate to convert to EUR.
    const cartTotalEur = cart.total_price / 100;

    // Update cache so watchForNewBars can pre-populate without a fetch
    cachedCartTotal = cartTotalEur;

    bars.forEach(el => updateBar(el, cartTotalEur));
  }


  /* ── Re-init bars injected by section re-renders ────────── */
  /*
     Handles the cart DRAWER: Focal Carbon replaces the entire
     mini-cart section HTML after every cart change. The new bar
     element is detected here and its message text is populated.

     The fill width is already correct from Liquid's inline style
     (data-cart-total / fsb_fill_pct computed server-side), so no
     transition hacks are needed — setting the same fill value via
     JS causes no visual change or animation.
  */

  function watchForNewBars() {
    const observer = new MutationObserver(function (mutations) {
      const newBars = [];

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;

          // Direct bar element
          if (node.classList && node.classList.contains('free-shipping-bar') && node.dataset.thresholdBase) {
            newBars.push(node);
          }

          // Bar nested inside a re-rendered section
          if (node.querySelectorAll) {
            node.querySelectorAll('.free-shipping-bar[data-threshold-base]').forEach(b => newBars.push(b));
          }
        }
      }

      if (newBars.length === 0) return;

      newBars.forEach(el => {
        // Liquid embeds data-cart-total when it renders this element.
        // Use it directly — it reflects the current cart state at render time
        // (Section Rendering API re-renders Liquid with the updated cart).
        // Fall back to cachedCartTotal if for any reason the attribute is absent.
        const liquidTotal = parseFloat(el.dataset.cartTotal);
        const total = !isNaN(liquidTotal) ? liquidTotal : cachedCartTotal;
        if (total !== null) updateBar(el, total);
      });

      // Async fetch to confirm and keep the cache current
      updateAllBars();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }


  /* ── Cart event listeners ───────────────────────────────── */
  /*
     Focal Carbon (Maestrooo) dispatches cart update events on
     document. We listen to every known variant so this works
     across theme versions and future updates.

     IMPORTANT: these events fire BEFORE Focal Carbon re-renders
     the section. By fetching the cart here, we update cachedCartTotal
     with the correct new value ahead of the re-render. When
     watchForNewBars() then sees the new bar element, it pre-populates
     using the already-correct cache — no wrong-state flash.
  */

  const CART_EVENTS = [
    'cart:updated',
    'cart:change',
    'cart:refresh',
    'cart-drawer:open',
    'cart-items:refresh',
    'cart-notification-product:show',
  ];

  CART_EVENTS.forEach(name => {
    document.addEventListener(name, () => updateAllBars());
  });

  // Also observe the cart count badge — if the number changes,
  // the cart changed. Reliable fallback for any events we may have missed.
  function watchCartCount() {
    const badge = document.querySelector('[data-cart-count], .cart-count, .cart__count, #CartCount');
    if (!badge) return;

    const observer = new MutationObserver(() => updateAllBars());
    observer.observe(badge, { childList: true, characterData: true, subtree: true });
  }


  /* ── Cart-page in-place update ──────────────────────────── */
  /*
     Focal Carbon calls window.location.reload() after every
     quantity change on the /cart page. We intercept it to do
     a smooth in-place DOM swap instead:

       1. Fade the cart section out (0.2s).
       2. Swap innerHTML with the pre-fetched section HTML (already
          in pendingCartUpdate, set by our fetch interceptor above).
       3. Fade the section back in — no page navigation, no blank flash.

     Dispatches cart:updated so Focal Carbon updates the cart count
     badge and any other derived UI.

     Falls back to a real reload (with fade) if no pre-fetched HTML
     is available (shouldn't happen in normal flow).
  */

  function patchCartReload() {
    const originalReload = window.location.reload.bind(window.location);
    let updating = false;

    const patchedReload = function () {
      if (updating) return;

      const wrapper = mainCartSectionEl;
      if (!wrapper) { originalReload(); return; }

      // Consume the cached update from the fetch interceptor
      const update = pendingCartUpdate;
      pendingCartUpdate = null;

      if (!update || !update.html) {
        // No pre-fetched HTML available — fall back to real reload with fade
        try { sessionStorage.setItem('fsb_cart_reload', '1'); } catch (e) {}
        wrapper.classList.add('fsb-section-exit');
        setTimeout(originalReload, 230);
        return;
      }

      updating = true;
      wrapper.classList.add('fsb-section-exit');

      setTimeout(function () {
        // Swap the section HTML while it's invisible (opacity: 0)
        wrapper.classList.remove('fsb-section-exit');
        wrapper.innerHTML = update.html;
        wrapper.classList.add('fsb-section-enter');

        // Let Focal Carbon update the cart count badge and other derived UI
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: update.cart }
        }));

        updating = false;
      }, 220);
    };

    // Modern browsers make window.location.reload non-writable in strict mode.
    // Try Object.defineProperty first, then direct assignment, then give up gracefully.
    try {
      Object.defineProperty(window.location, 'reload', {
        configurable: true,
        writable: true,
        value: patchedReload
      });
    } catch (e1) {
      try {
        window.location.reload = patchedReload; // fallback for older browsers
      } catch (e2) {
        console.warn('[FreeShippingBar] Could not patch location.reload — quantity changes will do full reloads:', e2.message);
      }
    }
  }


  /* ── Gift product image quick view ─────────────────────── */

  function initGiftImageViewer() {
    if (document.getElementById('fsb-gift-image-viewer')) return; // already set up

    // Inject styles
    var style = document.createElement('style');
    style.textContent = [
      '.free-shipping-bar__gift-product-img-btn{display:block;background:none;border:none;padding:0;cursor:zoom-in;position:relative;border-radius:6px;overflow:hidden;line-height:0;}',
      '.free-shipping-bar__gift-product-img-btn::after{content:"";position:absolute;inset:0;background:rgba(0,0,0,0);transition:background 0.15s;}',
      '.free-shipping-bar__gift-product-img-btn:hover::after,.free-shipping-bar__gift-product-img-btn:focus-visible::after{background:rgba(0,0,0,0.18);}',
      '.free-shipping-bar__gift-product-img-zoom{position:absolute;bottom:3px;right:3px;background:rgba(0,0,0,0.65);color:#fff;border-radius:3px;width:18px;height:18px;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:1;opacity:0.8;transition:opacity 0.15s;}',
      '.free-shipping-bar__gift-product-img-btn:hover .free-shipping-bar__gift-product-img-zoom,.free-shipping-bar__gift-product-img-btn:focus-visible .free-shipping-bar__gift-product-img-zoom{opacity:1;}',
      '#fsb-gift-image-viewer{max-width:min(92vw,600px);max-height:92dvh;border:none;border-radius:10px;padding:0;background:transparent;overflow:visible;}',
      '#fsb-gift-image-viewer::backdrop{background:rgba(0,0,0,0.82);backdrop-filter:blur(3px);}',
      '#fsb-gift-image-viewer img{display:block;max-width:min(92vw,600px);max-height:88dvh;width:auto;height:auto;border-radius:10px;object-fit:contain;}',
      '.fsb-gift-viewer-close{position:absolute;top:-14px;right:-14px;z-index:1;width:32px;height:32px;border-radius:50%;background:#fff;border:none;padding:0;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#111;box-shadow:0 2px 10px rgba(0,0,0,0.3);transition:transform 0.15s;}',
      '.fsb-gift-viewer-close:hover{transform:scale(1.12);}'
    ].join('');
    document.head.appendChild(style);

    // Inject dialog
    var dialog = document.createElement('dialog');
    dialog.id = 'fsb-gift-image-viewer';
    dialog.setAttribute('aria-label', 'Gift product image');
    dialog.innerHTML = '<button class="fsb-gift-viewer-close" aria-label="Close image"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button><img src="" alt="" loading="lazy">';
    document.body.appendChild(dialog);

    var img      = dialog.querySelector('img');
    var closeBtn = dialog.querySelector('.fsb-gift-viewer-close');

    // Focal Carbon's focus-trap uses clickOutsideDeactivates / allowOutsideClick which
    // checks if the clicked element has aria-controls === drawerEl.id. Without this, any
    // mousedown on our dialog triggers trap.deactivate() → the drawer closes before the
    // click even fires. Setting aria-controls to the drawer's own ID is the exact
    // exemption mechanism Focal Carbon provides for its own toggle buttons.
    var cartDrawerEl = document.querySelector('cart-drawer[id]');
    if (cartDrawerEl && cartDrawerEl.id) {
      dialog.setAttribute('aria-controls', cartDrawerEl.id);
      if (closeBtn) closeBtn.setAttribute('aria-controls', cartDrawerEl.id);
    }

    function openViewer(url, alt) { img.src = url; img.alt = alt; dialog.showModal(); }
    function closeViewer()         { dialog.close(); }
    dialog.addEventListener('close', function () { img.src = ''; });

    // Delegate — works across picker re-renders
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.free-shipping-bar__gift-product-img-btn');
      if (btn) openViewer(btn.dataset.imgUrl, btn.dataset.imgAlt || '');
    });

    // Close on backdrop click — stopPropagation prevents Focal Carbon's drawer-close handler from also firing
    dialog.addEventListener('click', function (e) {
      e.stopPropagation();
      var r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeViewer();
    });

    if (closeBtn) closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      closeViewer();
    });
  }


  /* ── Init ───────────────────────────────────────────────── */

  function init() {
    // -- DIAGNOSTIC --
    console.warn('[FSB init]', {
      path:       window.location.pathname,
      sectionEl:  mainCartSectionEl ? mainCartSectionEl.id : 'NOT FOUND',
      handle:     mainCartSectionHandle,
      bars:       document.querySelectorAll('.free-shipping-bar[data-threshold-base]').length
    });
    // ----------------

    // Fallback: if the AJAX swap was unavailable and a real reload happened,
    // the sessionStorage flag signals us to fade the section back in.
    try {
      if (sessionStorage.getItem('fsb_cart_reload')) {
        sessionStorage.removeItem('fsb_cart_reload');
        if (mainCartSectionEl) mainCartSectionEl.classList.add('fsb-section-enter');
      }
    } catch (e) {}

    // Populate message text immediately from the cart total Liquid embedded
    // in the HTML. This is synchronous — no fetch, no flash.
    // (Fill width is already correct from Liquid's inline style attribute.)
    document.querySelectorAll('.free-shipping-bar[data-threshold-base]').forEach(el => {
      const liquidTotal = parseFloat(el.dataset.cartTotal);
      if (isNaN(liquidTotal)) return;
      if (cachedCartTotal === null) cachedCartTotal = liquidTotal;
      updateBar(el, liquidTotal);
    });

    // Async fetch to verify and keep cache current for subsequent updates
    updateAllBars();
    watchCartCount();
    watchForNewBars();
    patchCartReload();
    initGiftImageViewer();

    // Close any open variant dropdowns when clicking elsewhere on the page.
    // Ignore clicks inside [data-fsb-select] (the button) or .fsb-variant-select__list
    // (the portalled list, now a direct child of body when open).
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-fsb-select]') || e.target.closest('.fsb-variant-select__list')) return;
      document.querySelectorAll('.fsb-variant-select__btn[aria-expanded="true"]').forEach(function (b) {
        if (b._fsbClose) b._fsbClose();
      });
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
